/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
//import { CommerceProperty, DisplayTemplate, ProductRefinerSource, ProductRefinerValue, RefinerType } from '@msdyn365-commerce/retail-proxy';
import { DisplayTemplate, ProductRefinerSource, ProductRefinerValue, RefinerType } from '@msdyn365-commerce/retail-proxy';
import { Button, Collapse, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import RangeRefineItem, { RangeRefineItemType } from './range-refine-item';
import RefineItem from './refine-item';
import { IRefineItemCommonProps } from './refine-item.props.common';
import { IRefineItemToggleNotification } from './refine-item-toggle-notification';
import { findMatchingRefinementCriterion, isRangeType } from './utilities';

/**
 * Properties associated with the RefineSubmenu component.
 */
export interface IRefineSubmenuProps {
    tempRangeTypeTODO: RangeRefineItemType;
    minValueSliderThumbAriaLabel?: string;
    maxValueSliderThumbAriaLabel?: string;
    productRefinerHierarchy: IProductRefinerHierarchy;
    selectedRefinerValues: ProductRefinerValue[];
    refineItemCommonProps: IRefineItemCommonProps;
    isDisabled: boolean;
    isExpandedOnInitialLoad: boolean;
    context: ICoreContext<IGeneric<IAny>>;
    moduleId: string;
    moduleTypeName: string;
    productCountAriaLabel?: string;
    singleProductCountAriaLabel?: string;
    isMobile?: boolean;

    /**
     * The telemetry content
     */
    telemetryContent?: ITelemetryContent;
    onUpdateRefiners(notfication: Readonly<IRefineItemToggleNotification>): void;
    urlBuilder(refiner: IRefineItemToggleNotification): string;
    config: any;
    expandCollapseAll?: string | boolean;
}

/**
 * Refine submenu state.
 */
export interface IRefineSubmenuState extends React.ComponentState {
    expanded: boolean;
}

/**
 *
 * The RefineSubmenu component renders the submenu category and child items.
 * This computed observes the stateful category hierarchy object.
 * @extends {React.PureComponent<IRefineSubmenuProps>}
 */
class RefineSubmenu extends React.Component<IRefineSubmenuProps, IRefineSubmenuState> {
    private localExpand: boolean = false;

    constructor(props: IRefineSubmenuProps) {
        super(props);

        this._onToggleItem = this._onToggleItem.bind(this);
        this._onToggleSubmenu = this._onToggleSubmenu.bind(this);

        const isExpanded = this.props.isExpandedOnInitialLoad;
        this.localExpand = false;

        // If (this.props.productRefinerHierarchy.DataTypeValue === ProductRefinerValueDataTypeValue.Range) {
        //     isExpanded = true;
        // }

        this.state = {
            expanded: isExpanded
        };
    }

    // public componentDidMount() {
    //     if (this.props.expandCollapseAll !== '') {
    //         console.log('We got here');
    //         this.setState({ isExpanded: this.props.expandCollapseAll });
    //     }
    // }

    public shouldComponentUpdate(nextProps: IRefineSubmenuProps, nextState: IRefineSubmenuState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public _renderExpandCollapseAll() {
        if (this.props.expandCollapseAll !== '') {
            if (this.state.expanded !== this.props.expandCollapseAll) {
                this.setState({ expanded: this.props.expandCollapseAll as any });
            }
        }
    }

    public render(): JSX.Element | null {
        const { productRefinerHierarchy, refineItemCommonProps } = this.props;

        console.log(productRefinerHierarchy);

        if (!productRefinerHierarchy) {
            refineItemCommonProps.telemetry.error('Cannot render submenu without refiner hierarchy data');
        }

        if (productRefinerHierarchy.SourceValue === ProductRefinerSource.Rating && this.props.context.app.config.hideRating) {
            return null;
        }

        if (!this.localExpand) {
            this._renderExpandCollapseAll();
        }

        this.localExpand = false;

        const setLearningArea = (): string => {
            return this.props.config.learningAreaName;
        };

        return (
            <div className={`ms-refine-submenu list-group ${productRefinerHierarchy.KeyName}`}>
                <Button
                    className={this.state.expanded ? 'ms-refine-submenu__toggle_expanded' : 'ms-refine-submenu__toggle_collapsed'}
                    aria-label={productRefinerHierarchy.KeyName || 'refiner.Name'}
                    onClick={this._onToggleSubmenu}
                    aria-expanded={this.state.expanded}
                >
                    {productRefinerHierarchy.KeyName === 'Category' ? setLearningArea() : productRefinerHierarchy.KeyName}
                </Button>

                <Collapse isOpen={this.state.expanded} timeout={350}>
                    {this._renderChildItems(productRefinerHierarchy)}
                </Collapse>
            </div>
        );
    }

    private _renderChildItems(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        if (isRangeType(productRefinerHierarchy.DataTypeValue)) {
            return this._renderRange(productRefinerHierarchy);
        }
        return this._renderSingleMultiSelect(productRefinerHierarchy);
    }

    private _renderSingleMultiSelect(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        const {
            isDisabled,
            refineItemCommonProps,
            selectedRefinerValues,
            context,
            productCountAriaLabel,
            singleProductCountAriaLabel,
            isMobile
        } = this.props;
        const isSingleSelect =
            productRefinerHierarchy.RefinerTypeValue === RefinerType.SingleSelect &&
            productRefinerHierarchy.SourceValue === ProductRefinerSource.Rating;
        const role = isSingleSelect ? { role: 'radiogroup' } : undefined;
        const refinerValuesList = productRefinerHierarchy.Values || [];
        const refinerValuesListSorted = refinerValuesList.sort((a, b) => {
            //deity sort
            const deityList = [
                'Ranginui',
                'Papatūānuku',
                'Tāne Mahuta',
                'Tūmatauenga',
                'Rongo',
                'Haumia-tiketike',
                'Tāwhirimātea',
                'Tangaroa',
                'Rūaumoko',
                'Whiro',
                'Hineahuone'
            ];

            const nameA = a.LeftValueBoundString?.toLowerCase() ?? '';
            const nameB = b.LeftValueBoundString?.toLowerCase() ?? '';
            const nameAindex = deityList.findIndex(deity => deity === nameA);
            const nameBindex = deityList.findIndex(deity => deity === nameB);

            if (nameAindex && nameBindex && nameAindex > 0 && nameBindex > 0) {
                return nameAindex - nameBindex;
            }

            switch (a.RefinerSourceValue) {
                case 1: {
                    //decide if the values are numeric
                    const aNumVal = Number(a.LeftValueBoundString?.charAt(0));
                    const bNumVal = Number(b.LeftValueBoundString?.charAt(0));

                    // numerical (alpha) sort
                    const aLen = a.LeftValueBoundString?.length;
                    const bLen = b.LeftValueBoundString?.length;

                    //both numbers are single digits.
                    if (aLen === 1 && aNumVal > 0 && aNumVal < 9 && bLen === 1 && bNumVal > 0 && bNumVal < 9) {
                        if (aNumVal < bNumVal) {
                            return -1;
                        }
                        if (aNumVal > bNumVal) {
                            return 1;
                        }
                        return 0;
                    }

                    //both numbers are hyphenated.
                    if (aLen === 3 && aNumVal > 0 && aNumVal < 9 && bLen === 3 && bNumVal > 0 && bNumVal < 9) {
                        const aSubNumVal = Number(a.LeftValueBoundString?.charAt(2));
                        const bSubNumVal = Number(b.LeftValueBoundString?.charAt(2));

                        if (aNumVal === bNumVal) {
                            if (aSubNumVal < bSubNumVal) {
                                return -1;
                            }
                            if (aSubNumVal > bSubNumVal) {
                                return 1;
                            }
                            return 0;
                        }
                        if (aNumVal < bNumVal) {
                            return -1;
                        }
                        if (aNumVal > bNumVal) {
                            return 1;
                        }
                        return 0;
                    }

                    // one number is hyphenated and the other is not.
                    // in this case swap is not required
                    if (aLen === 1 && aNumVal > 0 && aNumVal < 9 && bLen === 3 && bNumVal > 0 && bNumVal < 9) {
                        return -1;
                    }
                    // in this case swap is required
                    if (aLen === 3 && aNumVal > 0 && aNumVal < 9 && bLen === 1 && bNumVal > 0 && bNumVal < 9) {
                        return 1;
                    }

                    //default alpha sort
                    const nameA = a.LeftValueBoundString?.toLowerCase() ?? '';
                    const nameB = b.LeftValueBoundString?.toLowerCase() ?? '';
                    return nameA.localeCompare(nameB);
                }
                case 4:
                    return 0;
                default:
                    return 0;
            }
        });

        const listItems = refinerValuesListSorted.map((child: ProductRefinerValue, index: number) => {
            if (!child) {
                refineItemCommonProps.telemetry.error(
                    `[refine-submenu] Could not render refine item for refiner ${productRefinerHierarchy.RecordId} (${productRefinerHierarchy.KeyName})`
                );
                return null;
            }

            const selectedRefinementCriterion = findMatchingRefinementCriterion(child, selectedRefinerValues);

            return (
                <RefineItem
                    parentProductRefinerHierarchy={productRefinerHierarchy}
                    productRefinerValue={child}
                    selectedRefinementCriterion={selectedRefinementCriterion}
                    selectedRefinerValues={selectedRefinerValues.filter(selectedValue =>
                        refinerValuesList.find(value => value.RefinerRecordId === selectedValue.RefinerRecordId)
                    )}
                    refineItemCommonProps={refineItemCommonProps}
                    onToggle={this._onToggleItem}
                    urlBuilder={this.props.urlBuilder}
                    isDisabled={isDisabled}
                    key={index}
                    index={index}
                    context={context}
                    moduleId={this.props.moduleId}
                    moduleTypeName={this.props.moduleTypeName}
                    telemetryContent={this.props.telemetryContent}
                    productCountAriaLabel={productCountAriaLabel}
                    singleProductCountAriaLabel={singleProductCountAriaLabel}
                    isMobileView={isMobile}
                />
            );
        });
        return (
            <ul
                className={`ms-refine-submenu__list ${productRefinerHierarchy.KeyName}`}
                {...role}
                aria-label={productRefinerHierarchy.KeyName}
            >
                {listItems}
            </ul>
        );
    }

    private _getRangeType(productRefinerHierarchy: IProductRefinerHierarchy): 'input' | 'slider' {
        if (
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Range ||
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.Slider ||
            productRefinerHierarchy.DisplayTemplateValue === DisplayTemplate.SliderWithBars
        ) {
            return 'slider';
        }
        return 'input';
    }

    private _renderRange(productRefinerHierarchy: IProductRefinerHierarchy): JSX.Element | null {
        const {
            isDisabled,
            refineItemCommonProps,
            selectedRefinerValues,
            context,
            minValueSliderThumbAriaLabel,
            maxValueSliderThumbAriaLabel
        } = this.props;
        const submenuClassNamePrefix = 'ms-refine-submenu__item list-group-item refine-submenu__item';
        const refinerValuesList = productRefinerHierarchy.Values || [];
        const refinerValuesListSorted = refinerValuesList.sort((a, b) => {
            //deity sort
            const deityList = [
                'Ranginui',
                'Papatūānuku',
                'Tāne Mahuta',
                'Tūmatauenga',
                'Rongo',
                'Haumia-tiketike',
                'Tāwhirimātea',
                'Tangaroa',
                'Rūaumoko',
                'Whiro',
                'Hineahuone'
            ];

            const nameA = a.LeftValueBoundString?.toLowerCase() ?? '';
            const nameB = b.LeftValueBoundString?.toLowerCase() ?? '';
            const nameAindex = deityList.findIndex(deity => deity === nameA);
            const nameBindex = deityList.findIndex(deity => deity === nameB);

            if (nameAindex && nameBindex && nameAindex > 0 && nameBindex > 0) {
                return nameAindex - nameBindex;
            }

            switch (a.RefinerSourceValue) {
                case 1: {
                    //default alpha sort
                    const nameA = a.LeftValueBoundString?.toLowerCase() ?? '';
                    const nameB = b.LeftValueBoundString?.toLowerCase() ?? '';
                    return nameA.localeCompare(nameB);
                }
                case 4:
                    return 0;
                default:
                    return 0;
            }
        });

        const listItems = refinerValuesListSorted.map((child: ProductRefinerValue, index: number) => {
            if (!child) {
                refineItemCommonProps.telemetry.error(
                    `Could not render refine item for refiner ${productRefinerHierarchy.RecordId} (${productRefinerHierarchy.KeyName})`
                );
                return null;
            }

            const selectedRefinementCriterion = findMatchingRefinementCriterion(child, selectedRefinerValues);

            const rangeType = this._getRangeType(productRefinerHierarchy);
            const key = selectedRefinementCriterion
                ? `${selectedRefinementCriterion.LeftValueBoundString}-${selectedRefinementCriterion.RightValueBoundString}`
                : `not-selected-${index}`;
            return (
                <li className={`${submenuClassNamePrefix}--range`} key={index}>
                    <RangeRefineItem
                        parentProductRefinerHierarchy={productRefinerHierarchy}
                        productRefinerValue={child}
                        selectedRefinementCriterion={selectedRefinementCriterion}
                        refineItemCommonProps={refineItemCommonProps}
                        onToggle={this._onToggleItem}
                        urlBuilder={this.props.urlBuilder}
                        isDisabled={isDisabled}
                        rangeType={rangeType}
                        key={key}
                        sliderKey={key}
                        context={context}
                        minValueSliderThumbAriaLabel={minValueSliderThumbAriaLabel}
                        maxValueSliderThumbAriaLabel={maxValueSliderThumbAriaLabel}
                        moduleId={this.props.moduleId}
                        moduleTypeName={this.props.moduleTypeName}
                    />
                </li>
            );
        });
        return <ul className={`ms-refine-submenu__list list-unstyled ${productRefinerHierarchy.KeyName}`}>{listItems}</ul>;
    }

    private _onToggleItem(itemToggleNotification: IRefineItemToggleNotification): void {
        this.props.onUpdateRefiners(itemToggleNotification);
    }

    private _onToggleSubmenu(): void {
        console.log('ToggleItem');
        this.setState(prevState => ({
            expanded: !prevState.expanded
        }));
        this.localExpand = true;
    }

    // private _ToggleExpandCollapseSubmenu(refineExpandState: boolean): void {
    //     this.setState({
    //         expanded: refineExpandState
    //     });
    // }
}

export default RefineSubmenu;
